// Status → tone: which colour a state is shown in. A UI rule, not text (labels live in locales/).
import type { CylinderStatus, FillState, PositionStatus, Severity, ShipmentKind, ShipmentStatus, TestResult } from '@lib/api/common';
import type { GatewayCondition } from '@lib/api/gateways';
import type { HouseholdStatus } from '@lib/api/households';
import type { CheckResult, HubApplicationStatus, HubStatus } from '@lib/api/hubRegistration';
import type { EmptyCondition, OrderStatus } from '@lib/api/orders';
import type { OtaEventKind, OtaState } from '@lib/api/ota';

/** Status tones map 1:1 to chip/dot colours in the styles. Red ("critical") is for critical states only. */
export type Tone = 'green' | 'blue' | 'grey' | 'amber' | 'critical' | 'neutral' | 'violet';

/** Where a cylinder is; at the owner warehouse it is grey ("Di gudang") instead of green. */
export const POSITION_TONE: Record<PositionStatus, Tone> = { AT_LOCATION: 'green', IN_TRANSIT: 'blue', MISSING: 'critical', WITH_CONSUMER: 'green', WITH_COURIER: 'blue' };
export const WAREHOUSE_TONE: Tone = 'grey';

/** DESTROYED is final, not an alarm: grey like every other end of service. */
export const LIFECYCLE_TONE: Record<CylinderStatus, Tone> = { ACTIVE: 'neutral', WITHDRAWN: 'grey', DAMAGED: 'amber', LOST: 'critical', DESTROYED: 'grey' };

/** Full is ready to hand out (green); empty is the quiet default (neutral). */
export const FILL_TONE: Record<FillState, Tone> = { FULL: 'green', EMPTY: 'neutral' };

/**
 * A Surat Jalan's kind. Distribution is the default and stays unmarked (neutral); a withdrawal to a Bengkel Uji has its
 * own calm colour (violet: never a status colour), on the chip, the map's trucks and their legend.
 */
export const SHIPMENT_KIND_TONE: Record<ShipmentKind, Tone> = { DISTRIBUTION: 'neutral', WITHDRAWAL: 'violet' };

/** A Bengkel Uji verdict: passed green; failed grey (the cylinder's end, not an alarm). */
export const TEST_RESULT_TONE: Record<TestResult, Tone> = { PASS: 'green', FAIL: 'grey' };

export const SHIPMENT_STATUS_TONE: Record<ShipmentStatus, Tone> = {
  LOADING: 'blue',
  IN_TRANSIT: 'blue',
  RECEIVED: 'green',
  RECEIVED_WITH_DISCREPANCY: 'amber',
  CANCELLED: 'grey',
};

/**
 * A Surat Jalan as the Surat Jalan pages show it (one more state than its status: past the expected arrival). Calm:
 * only the trip in motion is blue, a look needed is amber, red never (a missing cylinder is the cylinder's own red).
 */
export const SHIPMENT_STATE_TONE = { loading: 'neutral', inTransit: 'blue', late: 'amber', unloading: 'blue', received: 'green', discrepancy: 'amber', cancelled: 'grey' } as const satisfies Record<string, Tone>;

/** A reader's condition (the server's): normal green, a problem amber, disconnected red, never connected grey. */
export const GATEWAY_CONDITION_TONE: Record<GatewayCondition, Tone> = { normal: 'green', attention: 'amber', offline: 'critical', never_connected: 'grey' };

export const SEVERITY_TONE: Record<Severity, Tone> = { critical: 'critical', warning: 'amber', info: 'blue' };

/**
 * A gateway's update state (OtaState). Waiting is grey, under way blue, done green; a failure that is retried, or a
 * gateway that protected itself by rolling back, is amber; red only once the server stopped trying.
 */
export const OTA_STATE_TONE: Record<OtaState, Tone> = {
  none: 'neutral',
  queued: 'grey',
  waiting_window: 'grey',
  offline: 'grey',
  sent: 'blue',
  in_progress: 'blue',
  installed: 'green',
  failed: 'amber',
  exhausted: 'critical',
  rolled_back: 'amber',
};

/** A step in a gateway's update history, in the same colours as the states it leads to. */
export const OTA_EVENT_TONE: Record<OtaEventKind, Tone> = {
  targeted: 'grey',
  sent: 'blue',
  reported_in_progress: 'blue',
  installed: 'green',
  failed: 'amber',
  rolled_back: 'amber',
  cancelled: 'grey',
  exhausted: 'critical',
};

/**
 * A Hub application's status. Calm: waiting on staff is neutral, waiting on the applicant (a revision) amber, under way
 * (visit, decision, agreement) blue, active green, closed grey. Red never: a rejection is an outcome, not an alarm.
 */
export const HUB_APPLICATION_STATUS_TONE: Record<HubApplicationStatus, Tone> = {
  SUBMITTED: 'neutral',
  NEEDS_REVISION: 'amber',
  DOCS_OK: 'blue',
  VISIT_SCHEDULED: 'blue',
  VISITED: 'blue',
  VERIFIED: 'blue',
  APPROVED: 'blue',
  AGREEMENT_SIGNED: 'blue',
  ACTIVE: 'green',
  REJECTED: 'grey',
  WITHDRAWN: 'grey',
};
/** An active Hub's standing: suspended amber, terminated critical (its cylinders must be recovered). */
export const HUB_STATUS_TONE: Record<HubStatus, Tone> = { ACTIVE: 'green', SUSPENDED: 'amber', TERMINATED: 'critical' };
/** A checklist answer: passed green, failed amber (fixable or a reason to reject), not applicable neutral. */
export const CHECK_RESULT_TONE: Record<CheckResult, Tone> = { PASS: 'green', FAIL: 'amber', NOT_APPLICABLE: 'neutral' };

/** An order: work to do is blue, waiting at the counter blue, a failed attempt amber, done green, cancelled grey. */
export const ORDER_STATUS_TONE: Record<OrderStatus, Tone> = {
  PLACED: 'blue',
  ACCEPTED: 'blue',
  OUT_FOR_DELIVERY: 'blue',
  READY_FOR_PICKUP: 'blue',
  DELIVERY_FAILED: 'amber',
  COMPLETED: 'green',
  CANCELLED: 'grey',
};
/** A household: waiting for the Hub amber (an action is due), registered green, rejected/suspended grey. */
export const HOUSEHOLD_STATUS_TONE: Record<HouseholdStatus, Tone> = { PENDING_VERIFICATION: 'amber', REGISTERED: 'green', REJECTED: 'grey', SUSPENDED: 'grey' };
/** The empty handed back: fine is quiet, damaged or missing need a look (amber), never red. */
export const EMPTY_CONDITION_TONE: Record<EmptyCondition, Tone> = { GOOD: 'neutral', DAMAGED: 'amber', MISSING: 'amber' };
